import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';
import { Loader2 } from 'lucide-react';
import { appId, db } from '@/lib/firebase';
import { createCheckoutSession, type BillingType, type ProductGroup } from '@/lib/stripe-billing';

interface CustomerServicesPageProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

interface ProductPrice {
  id: string;
  unitAmount: number;
  currency: string;
  billingType: BillingType;
}

interface ServiceProduct {
  id: string;
  name: string;
  description: string;
  group: ProductGroup;
  visible: boolean;
  displayOrder: number;
  active: boolean;
  prices: ProductPrice[];
}

const GROUP_TITLES: Record<ProductGroup, string> = {
  va_services: 'Virtual Assistant Services',
  saas_subscriptions: 'SaaS Subscription Plans',
  one_time_services: 'On-Demand Services',
};

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function labelForBillingType(type: BillingType): string {
  if (type === 'one_time') return 'One-time';
  if (type === 'monthly') return 'Monthly';
  if (type === '6_month') return 'Every 6 months';
  return 'Yearly';
}

export function CustomerServicesPage({ showNotify }: CustomerServicesPageProps): JSX.Element {
  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [publishableKey, setPublishableKey] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [activeBillingType, setActiveBillingType] = useState<BillingType>('monthly');
  const [purchasingPriceId, setPurchasingPriceId] = useState<string | null>(null);

  useEffect(() => {
    const productsQuery = query(
      collection(db, 'artifacts', appId, 'stripeProducts'),
      where('visible', '==', true),
      orderBy('displayOrder', 'asc')
    );
    const settingsRef = collection(db, 'artifacts', appId, 'settings');

    const unsubs: Array<() => void> = [];
    unsubs.push(
      onSnapshot(
        productsQuery,
        (snap) => {
          const mapped: ServiceProduct[] = snap.docs.map((docSnap) => {
            const data = docSnap.data() as Partial<ServiceProduct>;
            return {
              id: docSnap.id,
              name: data.name ?? 'Untitled Service',
              description: data.description ?? '',
              group: data.group ?? 'one_time_services',
              visible: data.visible ?? false,
            displayOrder: typeof data.displayOrder === 'number' ? data.displayOrder : 999,
              active: data.active ?? true,
              prices: Array.isArray(data.prices) ? (data.prices as ProductPrice[]) : [],
            };
          });
          setProducts(mapped.filter((product) => product.active));
          setLoading(false);
        },
        () => {
          setProducts([]);
          setLoading(false);
        }
      )
    );

    unsubs.push(
      onSnapshot(query(settingsRef, where('__name__', '==', 'stripe')), (snap) => {
        const key = (snap.docs[0]?.data()?.publishableKey as string | undefined)?.trim() ?? '';
        setPublishableKey(key);
      })
    );

    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  const grouped = useMemo(() => {
    return {
      va_services: products.filter((product) => product.group === 'va_services'),
      saas_subscriptions: products.filter((product) => product.group === 'saas_subscriptions'),
      one_time_services: products.filter((product) => product.group === 'one_time_services'),
    };
  }, [products]);

  const handleCheckout = async (priceId: string): Promise<void> => {
    setPurchasingPriceId(priceId);
    try {
      const successUrl = `${window.location.origin}/dashboard/services?checkout=success`;
      const cancelUrl = `${window.location.origin}/dashboard/services?checkout=cancel`;
      const session = await createCheckoutSession(priceId, successUrl, cancelUrl);
      if (publishableKey) {
        const stripe = await loadStripe(publishableKey);
        if (!stripe || !session.sessionUrl) {
          throw new Error('[CustomerServicesPage] Failed to initialize Stripe checkout.');
        }
      }
      window.location.href = session.sessionUrl;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to start checkout.';
      showNotify?.(message, 'error');
    } finally {
      setPurchasingPriceId(null);
    }
  };

  const renderProductCard = (product: ServiceProduct, onlyBillingType?: BillingType): JSX.Element => {
    const prices = onlyBillingType
      ? product.prices.filter((price) => price.billingType === onlyBillingType)
      : product.prices;
    const displayPrices = prices.length > 0 ? prices : product.prices;

    return (
      <div key={product.id} className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-base font-semibold text-gray-900">{product.name}</h3>
        <p className="text-sm text-gray-600 mt-1">{product.description || 'No description available yet.'}</p>
        <div className="mt-4 space-y-2">
          {displayPrices.length === 0 ? (
            <p className="text-sm text-gray-500">Pricing will be published soon.</p>
          ) : (
            displayPrices.map((price) => (
              <div
                key={price.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
              >
                <p className="text-sm font-medium text-gray-900">
                  {formatMoney(price.unitAmount, price.currency)} · {labelForBillingType(price.billingType)}
                </p>
                <button
                  type="button"
                  onClick={() => handleCheckout(price.id)}
                  disabled={purchasingPriceId === price.id}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {purchasingPriceId === price.id ? <Loader2 size={13} className="animate-spin" /> : null}
                  {price.billingType === 'one_time' ? 'Purchase' : 'Subscribe'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 space-y-8">
      <section className="bg-white border border-gray-200 rounded-2xl p-6">
        <h1 className="text-2xl font-bold text-gray-900">Available Services</h1>
        <p className="text-sm text-gray-600 mt-1">
          Choose from recurring VA plans, SaaS subscriptions, or one-time on-demand services.
        </p>
      </section>

      {loading ? (
        <section className="bg-white border border-gray-200 rounded-2xl p-8 text-center text-sm text-gray-600">
          Loading services...
        </section>
      ) : (
        <>
          <section className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-lg font-semibold text-gray-900">{GROUP_TITLES.va_services}</h2>
              <p className="text-sm text-gray-600 mt-1">
                Select a billing cadence for VA services and subscribe securely with Stripe Checkout.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(['monthly', '6_month', 'yearly'] as BillingType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setActiveBillingType(type)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                      activeBillingType === type
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {labelForBillingType(type)}
                  </button>
                ))}
              </div>
            </div>
            {grouped.va_services.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-600">
                No VA service plans are published yet.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {grouped.va_services.map((product) => renderProductCard(product, activeBillingType))}
              </div>
            )}
          </section>

          <section className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-lg font-semibold text-gray-900">{GROUP_TITLES.one_time_services}</h2>
              <p className="text-sm text-gray-600 mt-1">
                Purchase one-time services directly through secure Stripe checkout.
              </p>
            </div>
            {grouped.one_time_services.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-sm text-gray-600">
                No one-time services are available yet.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {grouped.one_time_services.map((product) => renderProductCard(product, 'one_time'))}
              </div>
            )}
          </section>

          <section className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h2 className="text-lg font-semibold text-gray-900">{GROUP_TITLES.saas_subscriptions}</h2>
              <p className="text-sm text-gray-600 mt-1">
                SaaS plans are rolling out soon. Published plans will appear here automatically.
              </p>
            </div>
            {grouped.saas_subscriptions.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-xl p-6 text-sm text-gray-600">
                Coming soon.
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {grouped.saas_subscriptions.map((product) => renderProductCard(product))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
